import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, type FeatureLevel, type PersonRow, type RolesResponse } from "@/lib/api";
import {
  ADMIN_ROLE,
  adminFloor,
  chatGroups,
  customGroups,
  featureCopy,
  featureLevel,
  gridFeatures,
  groupLabel,
  hasRole,
  PROBLEM_REPORTS,
  STANDARD_ROLE,
  withLevel,
} from "@/lib/people-levels";
import { cn } from "@/lib/utils";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Info, Plus, Search, Settings, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FeatureRow, NewGroupDialog } from "./GroupParts";
import { peopleKey, rolesKey } from "./use-identity";

const rolesMutationKey = (containerId: string) => ["boo-roles-save", containerId];

function useSaveRoles(containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: rolesMutationKey(containerId),
    mutationFn: (next: RolesResponse) => api.containers.roles.put(containerId, { roles: next.roles }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: rolesKey(containerId) });
      const prev = queryClient.getQueryData<RolesResponse>(rolesKey(containerId));
      queryClient.setQueryData(rolesKey(containerId), next);
      return { prev };
    },
    onError: (err, _next, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(rolesKey(containerId), ctx.prev);
      toast.error(err instanceof Error ? err.message : "That didn't save. Try again.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: rolesKey(containerId) }),
  });
}

// Group settings and the Boo-wide switch write the same roles document, so
// each stays disabled while the other saves.
function useRolesBusy(containerId: string): boolean {
  return useIsMutating({ mutationKey: rolesMutationKey(containerId) }) > 0;
}

export function GroupSettings({
  containerId,
  data,
  people,
  canEdit,
  onShowPeople,
  onDeleted,
}: {
  containerId: string;
  data: RolesResponse;
  // null until the people list has loaded; counts stay hidden and Delete
  // group stays off until then.
  people: PersonRow[] | null;
  canEdit: boolean;
  onShowPeople: (group: string) => void;
  onDeleted: (group: string) => void;
}) {
  const queryClient = useQueryClient();
  const save = useSaveRoles(containerId);
  const rolesBusy = useRolesBusy(containerId);
  // The selected group lives in the URL (?group=), so it never carries over
  // to another Boo.
  const params = useParams({ strict: false }) as { orgId?: string };
  const search = useSearch({ strict: false }) as { group?: string };
  const navigate = useNavigate();
  const selected = search.group ?? null;
  const setSelected = (group: string | null, replace = false) => {
    void navigate({
      to: "/orgs/$orgId/containers/$containerId/people",
      params: { orgId: params.orgId ?? "", containerId },
      search: group ? { tab: "groups", group } : { tab: "groups" },
      replace,
    });
  };
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [findGroup, setFindGroup] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!selected) return;
    if (window.matchMedia("(max-width: 639px)").matches) headingRef.current?.focus();
  }, [selected]);

  const deleteGroup = useMutation({
    mutationKey: rolesMutationKey(containerId),
    mutationFn: async (group: string) => {
      const latest = queryClient.getQueryData<RolesResponse>(rolesKey(containerId)) ?? data;
      const next = { ...latest.roles };
      delete next[group];
      await api.containers.roles.put(containerId, { roles: next });
    },
    onSuccess: (_d, group) => {
      setDeleting(false);
      toast.success(`Deleted ${group}.`);
      setSelected(null, true);
      onDeleted(group);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "That didn't delete. Try again."),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: rolesKey(containerId) });
      queryClient.invalidateQueries({ queryKey: peopleKey(containerId) });
    },
  });

  const custom = customGroups(data.roles);
  const features = gridFeatures(data.features);
  const current = selected && (selected === ADMIN_ROLE || selected === STANDARD_ROLE || hasRole(data.roles, selected)) ? selected : ADMIN_ROLE;
  const chosen = current === selected;
  const countOf = (g: string): number | null =>
    !people
      ? null
      : g === STANDARD_ROLE
      ? people.length
      : g === ADMIN_ROLE
        ? people.filter((p) => p.base === "admin").length
        : people.filter((p) => chatGroups(p).includes(g)).length;
  const isBuiltIn = current === ADMIN_ROLE || current === STANDARD_ROLE;
  const currentCount = countOf(current);

  const q = findGroup.trim().toLowerCase();
  const showFind = custom.length + 2 > 8;
  const listed = (g: string) => !q || groupLabel(g).toLowerCase().includes(q);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="groups-heading">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 id="groups-heading" className="text-base font-semibold">
            What each group can do in chat
          </h2>
          <p className="text-sm text-muted-foreground text-pretty">
            Everyone gets Standard. Put people in a group to give them more. If someone is in several groups, they get the
            most from each.
          </p>
        </div>
        <SaveState pending={save.isPending} saved={save.isSuccess} />
      </div>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <aside
          className={cn(
            "w-full shrink-0 flex-col overflow-hidden rounded-lg border border-border sm:flex sm:w-60",
            chosen ? "hidden" : "flex",
          )}
          aria-label="Groups"
        >
          {(canEdit || showFind) && (
            <div className="flex flex-col gap-2 border-b border-border p-3">
              {canEdit && (
                <Button variant="outline" size="sm" className="w-full justify-center gap-2" onClick={() => setCreating(true)}>
                  <Plus className="size-4" />
                  New group
                </Button>
              )}
              {showFind && (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <Input
                    value={findGroup}
                    onChange={(e) => setFindGroup(e.target.value)}
                    placeholder="Find group"
                    aria-label="Find group"
                    className="h-8 pl-8 text-sm"
                  />
                </div>
              )}
            </div>
          )}
          <ul className="flex flex-col divide-y divide-border" onKeyDown={arrowNav}>
            {[ADMIN_ROLE, STANDARD_ROLE].filter(listed).map((g) => (
              <GroupItem key={g} group={g} count={countOf(g)} active={g === current} chosen={g === selected} onSelect={() => setSelected(g)} />
            ))}
            {custom.filter(listed).length > 0 && <li role="separator" className="h-2 bg-muted/50" />}
            {custom.filter(listed).map((g) => (
              <GroupItem key={g} group={g} count={countOf(g)} active={g === current} chosen={g === selected} onSelect={() => setSelected(g)} />
            ))}
            {q && ![ADMIN_ROLE, STANDARD_ROLE, ...custom].some(listed) && (
              <li className="px-4 py-2.5 text-xs text-muted-foreground">No group matches “{findGroup.trim()}”.</li>
            )}
          </ul>
        </aside>

        <div
          role="region"
          className={cn("min-w-0 flex-1 flex-col gap-6 sm:flex", chosen ? "flex" : "hidden")}
          aria-labelledby="group-heading"
        >
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground sm:hidden"
          >
            <ArrowLeft className="size-4" />
            All groups
          </button>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 id="group-heading" ref={headingRef} tabIndex={-1} className="text-base font-semibold [overflow-wrap:anywhere] focus:outline-none">
                {groupLabel(current)}
              </h3>
              {isBuiltIn && (
                <p className="text-sm text-muted-foreground text-pretty">
                  {current === ADMIN_ROLE
                    ? "Owners and admins of this Boo."
                    : "What everyone gets, including people who only talk to Boo in chat."}
                </p>
              )}
              {currentCount !== null && (
                <p className="text-xs text-muted-foreground">
                  <span className="tabular-nums">{countText(currentCount)}</span>
                  {!isBuiltIn && currentCount > 0 && (
                    <>
                      {" · "}
                      <button
                        type="button"
                        onClick={() => onShowPeople(current)}
                        className="underline-offset-4 hover:text-foreground hover:underline"
                      >
                        Show these people
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
            {canEdit && !isBuiltIn && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button size="icon-sm" variant="ghost" aria-label={`Options for ${current}`}>
                    <Settings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem variant="destructive" disabled={currentCount === null} onSelect={() => setDeleting(true)}>
                    Delete group
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {features.map((f) => (
              <FeatureRow
                key={f.id}
                feature={f}
                groupName={groupLabel(current)}
                value={featureLevel(data.roles, current, f.id)}
                floor={current === ADMIN_ROLE ? adminFloor(data.roles, f.id) : null}
                editable={canEdit}
                busy={rolesBusy}
                onChange={(l) => save.mutate({ ...data, roles: withLevel(data.roles, current, f.id, l) })}
              />
            ))}
          </div>
        </div>
      </div>

      {creating && (
        <NewGroupDialog
          existing={Object.keys(data.roles)}
          busy={rolesBusy}
          onClose={() => setCreating(false)}
          onAdd={(name, from) => {
            const base = (hasRole(data.roles, from) ? data.roles[from] : undefined) ?? data.roles[STANDARD_ROLE];
            save.mutate(
              { ...data, roles: { ...data.roles, [name]: { features: { ...base.features } } } },
              {
                onSuccess: () => {
                  setCreating(false);
                  toast.success(`Created ${name}.`);
                  setSelected(name);
                },
              },
            );
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={(o) => !o && !deleteGroup.isPending && setDeleting(false)}
        title={`Delete ${groupLabel(current)}?`}
        description={`${countText(currentCount ?? 0)} will lose what ${groupLabel(current)} gives them.`}
        confirmLabel="Delete group"
        loading={rolesBusy}
        onConfirm={() => !rolesBusy && deleteGroup.mutate(current)}
      />
    </section>
  );
}

// Settings that apply to everyone on the Boo, whatever their group.
export function AdvancedSettings({
  containerId,
  data,
  canEdit,
}: {
  containerId: string;
  data: RolesResponse;
  canEdit: boolean;
}) {
  const save = useSaveRoles(containerId);
  const rolesBusy = useRolesBusy(containerId);
  const [open, setOpen] = useState(false);
  const problemReports = data.features.find((f) => f.id === PROBLEM_REPORTS);
  if (!problemReports) return null;

  const reportsFrom = Object.keys(data.roles).filter((r) => featureLevel(data.roles, r, PROBLEM_REPORTS) !== "disabled");
  const reportsOn = reportsFrom.length === Object.keys(data.roles).length;
  const reportsMixed = !reportsOn && reportsFrom.length > 0;
  const setEveryone = (feature: string, level: FeatureLevel) => {
    let next = data.roles;
    for (const role of Object.keys(data.roles)) next = withLevel(next, role, feature, level);
    save.mutate({ ...data, roles: next });
  };

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <section className="flex flex-col gap-3" aria-labelledby="advanced-heading">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <h2 id="advanced-heading" className="text-base font-semibold">
            <CollapsibleTrigger className="group inline-flex items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
              Advanced
            </CollapsibleTrigger>
          </h2>
          {!open && reportsMixed && (
            <span className="mr-auto text-xs text-amber-700 dark:text-amber-400">
              Problem reports are on for some groups only
            </span>
          )}
          <SaveState pending={save.isPending} saved={save.isSuccess} />
        </div>
        <CollapsibleContent className="flex flex-col gap-3">
          <div
            role="note"
            className="flex items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <p className="text-pretty">
              These apply to everyone on this Boo, whatever their group. Change them only if you know you need to.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{featureCopy(problemReports).title}</p>
              <p className="text-xs text-muted-foreground text-pretty">{featureCopy(problemReports).summary}</p>
              {reportsMixed && (
                <p id="problem-reports-mixed" className="mt-1 text-xs text-amber-700 text-pretty dark:text-amber-400">
                  Right now only {reportsFrom.map(groupLabel).join(", ")} send them.
                </p>
              )}
            </div>
            <Switch
              checked={reportsOn}
              aria-describedby={reportsMixed ? "problem-reports-mixed" : undefined}
              disabled={!canEdit || rolesBusy}
              onCheckedChange={(on) => setEveryone(PROBLEM_REPORTS, on ? "auto" : "disabled")}
              aria-label={featureCopy(problemReports).title}
            />
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    return;
  }
}

export function UpgradeNotices({ containerId, data, isOwner }: { containerId: string; data: RolesResponse; isOwner: boolean }) {
  const upgradeKey = `boo.upgradeNotice.${containerId}`;
  const droppedKey = `boo.upgradeNotice.${containerId}.dropped`;
  const [upgradeSeen, setUpgradeSeen] = useState(() => readFlag(upgradeKey));
  const [droppedSeen, setDroppedSeen] = useState(() => readFlag(droppedKey));
  const dropped = data.upgrade?.dropped ?? [];
  return (
    <>
      {data.upgrade && !upgradeSeen && (
        <Banner
          tone="info"
          onDismiss={() => {
            writeFlag(upgradeKey);
            setUpgradeSeen(true);
          }}
        >
          We moved your Extras settings into the Groups tab. Everyone can do exactly what they
          could before.
        </Banner>
      )}
      {dropped.length > 0 && isOwner && !droppedSeen && (
        <Banner
          tone="warn"
          onDismiss={() => {
            writeFlag(droppedKey);
            setDroppedSeen(true);
          }}
        >
          <span className="font-medium">Some earlier rules for this Boo couldn&apos;t move here:</span>
          <ul className="mt-1 list-disc pl-5">
            {dropped.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
          <span className="mt-1 block">Everyone now gets what their group allows on the Groups tab. Check that each group looks right.</span>
        </Banner>
      )}
    </>
  );
}

function countText(n: number): string {
  return n === 1 ? "1 person" : `${n} people`;
}

function arrowNav(e: React.KeyboardEvent<HTMLUListElement>) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const buttons = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-group]")];
  const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (at === -1) return;
  e.preventDefault();
  buttons[(at + (e.key === "ArrowDown" ? 1 : buttons.length - 1)) % buttons.length]?.focus();
}

function GroupItem({
  group,
  count,
  active,
  chosen,
  onSelect,
}: {
  group: string;
  count: number | null;
  active: boolean;
  chosen: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        data-group={group}
        aria-current={active ? "true" : undefined}
        aria-label={count === null ? groupLabel(group) : `${groupLabel(group)}, ${countText(count)}`}
        onClick={onSelect}
        className={cn(
          "group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "hover:bg-muted/30 active:bg-muted/50",
          active && (chosen ? "bg-muted/60 hover:bg-muted/60" : "sm:bg-muted/60 sm:hover:bg-muted/60"),
        )}
      >
        <span
          className={cn("min-w-0 flex-1 truncate text-sm", active && (chosen ? "font-medium" : "sm:font-medium"))}
          title={groupLabel(group)}
        >
          {groupLabel(group)}
        </span>
        {count !== null && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground" aria-hidden>
            {count}
          </span>
        )}
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    </li>
  );
}

function SaveState({ pending, saved }: { pending: boolean; saved: boolean }) {
  if (!pending && !saved) return null;
  return (
    <span className="inline-flex items-center gap-1.5 pb-1 text-xs text-muted-foreground" aria-live="polite">
      {pending ? (
        "Saving…"
      ) : (
        <>
          <Check className="size-3.5" />
          Saved
        </>
      )}
    </span>
  );
}

function Banner({
  tone,
  onDismiss,
  children,
}: {
  tone: "info" | "warn";
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  const Icon = tone === "info" ? Info : TriangleAlert;
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
        tone === "info"
          ? "border-border bg-muted/40 text-foreground"
          : "border-amber-300/50 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200",
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1 text-pretty">{children}</div>
      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
